import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../../components/EditProductDescription/EditProductDescription.css";
import EditProductLeft from "../../components/EditProductDescription/EditProductLeft";
import ProductImage from "../../components/EditProductDescription/ProductImage";
import ProductDetailsContainer from "../../components/EditProductDescription/ProductDetailsContainer";
import ProductStoryModal from "../../components/EditProductDescription/ProductStoryModal";
import { BiLaptop, BiMobile } from "react-icons/bi";
import Breadcrumb from "../../components/Common/Bredcrump";
import {
  generateProductDescription,
  feedback,
  generateProductDescriptionStream,
  productSaveAsDraft,
  updateProduct,
  updateProductSafely,
  updateProductMinimal,
  checkProductExists,
  checkProductExistsBySku,
  findExistingProduct,
  fetchProductBySku,
  fetchProductById,
  fetchShopifyProductById,
  fetchAmazonProductById,
  fetchAmazonProductBySku,
} from "../../utils/Product";
import Loader from "../../components/Common/Loader";
import "../ProductDescription/ProductDescription.css";
import { FaRegThumbsDown, FaRegThumbsUp } from "react-icons/fa";
import PreviewProductDescription from "../Preview/PreviewProductDescription";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";
import NameChangeModal from "../../components/EditProductDescription/NameChangeModal";
import LocalDraftModal from "../../components/EditProductDescription/LocalDraftModal";
import SequenceTemplateModal from "../../components/EditProductDescription/SequenceTemplateModal";
import {
  manageAgentSessionIds
} from "../../helpers/index";
import { useProductContext } from "../../components/Context/ProductContext";
import { useCompany } from "../../context/CompanyContext";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../utils/api";
import { translateContent } from "../../services/translationService";

const EditProductDescription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId: urlProductId } = useParams();
  const { companyKnowledgeDocs, loadCompanyKnowledgeBase } = useCompany();

  const initialSku = location.state?.sku || "";
  const initialProductData = location.state?.productData || null;
  const initialProductId = urlProductId || location.state?.productId || null;
  const initialTags = Array.isArray(location.state?.tags)
    ? location.state.tags
    : [];
  const [showStoryModal, setShowStoryModal] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isLoading, setIsLoading] = useState(!initialProductData);
  const [productData, setProductData] = useState(initialProductData);
  const [productImages, setProductImages] = useState(
    initialProductData?.images || []
  );
  const [error, setError] = useState(null);
  const [keywords, setKeywords] = useState(
    initialProductData?.keywords
      ? initialProductData.keywords.split(",").map((k) => k.trim())
      : initialTags
  );
  const [details, setDetails] = useState(
    initialProductData?.further_details || ""
  );
  const [ingredients, setIngredients] = useState(
    initialProductData?.ingredients || ""
  );
  const [howToUse, setHowToUse] = useState(
    initialProductData?.how_to_use || ""
  );
  const [dosage, setDosage] = useState(initialProductData?.dosage || "");
  const [language, setLanguage] = useState(
    initialProductData?.language
      ? {
          label: initialProductData.language,
          value: initialProductData.language,
        }
      : null
  );
  const [locationFocus, setLocationFocus] = useState(
    initialProductData?.location
      ? {
          label: initialProductData.location,
          value: initialProductData.location,
        }
      : null
  );
  // Force re-render by changing the key prop
  const [renderKey, setRenderKey] = useState(0);
  const { previewMode, setPreviewMode } = useProductContext();
  const [responseId, setResponseId] = useState("");
  // const [previewData, setPreviewData] = useState({
  //   productName: productData?.title,
  //   productabout: productData?.teaser,
  //   HeroImage: productData?.image,
  //   ProductImages: productData?.images,
  //   section: [],
  // });
  const [showFeedback, setShowFeedback] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { agentId: globalAgentId, sessionId: globalSessionId } =
    manageAgentSessionIds();
  const [activeAgentId, setActiveAgentId] = useState(globalAgentId);
  const [activeSessionId, setActiveSessionId] = useState(globalSessionId);
  const [isStreaming, setIsStreaming] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState([]);
  const [streamingLog, setStreamingLog] = useState([]); // For streaming response display
  const streamingLogRef = React.useRef(null);
  const [accumulatedStreamData, setAccumulatedStreamData] = useState({}); // Store complete streaming response
  const [heroImage, setHeroImage] = useState(initialProductData?.image || null);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [productImageFiles, setProductImageFiles] = useState([]);
  const [savedProductId, setSavedProductId] = useState(null);
  const [showNameChangeModal, setShowNameChangeModal] = useState(false);
  const [showLocalDraftModal, setShowLocalDraftModal] = useState(false);
  const [showSequenceTemplateModal, setShowSequenceTemplateModal] = useState(false);
  const [currentSectionOrder, setCurrentSectionOrder] = useState([]);
  const hasLoadedRef = useRef(false);
  // const [lastChunk, setLastChunk] = useState(null); // Debug: show last streaming chunk

  // Initialize savedProductId if there's existing product data or passed productId
  useEffect(() => {
    if (initialProductId) {
      setSavedProductId(initialProductId);
      console.log("Set savedProductId from location state:", initialProductId);
    } else if (initialProductData && initialProductData.id) {
      setSavedProductId(initialProductData.id);
      console.log(
        "Set savedProductId from productData:",
        initialProductData.id
      );
    }
  }, [initialProductId, initialProductData]);

  // Load company knowledge base when component mounts
  useEffect(() => {
    loadCompanyKnowledgeBase();
  }, [loadCompanyKnowledgeBase]);

  // Handle keywords change
  const handleKeywordsChange = (newKeywords) => {
    if (Array.isArray(newKeywords)) {
      setKeywords(newKeywords);
    }
  };

  // Handle hero image change
  const handleHeroImageChange = (imageDataUrl, file) => {
    setHeroImage(imageDataUrl);
    setHeroImageFile(file);
  };

  // Handle product images change
  const handleProductImagesChange = (images, file) => {
    setProductImages(images);
    if (file) {
      setProductImageFiles((prev) => [...prev, file]);
    }
  };

  const handlePreviewToggle = (mode) => {
    setPreviewMode((prevMode) => (prevMode === mode ? null : mode));
  };

  const onTranslate = async () => {
    // Check if we have product data to translate
    if (!productData && !initialProductData) {
      toast.error("Please generate product content first before translating");
      return;
    }

    const languageValue = language?.label || language || "";
    const locationValue = locationFocus?.label || locationFocus || "";
    
    if (!languageValue) {
      toast.error("Please select a target language for translation");
      return;
    }

    setIsLoading(true);
    setIsStreaming(true);
    setRegeneratingSection([]); // Clear any previous regenerating sections
    
    try {
      // Prepare the product data to send for translation
      const currentProduct = productData || initialProductData;
      
      // Check if this is an Amazon product
      const productSource = initialProductData?.source || productData?.source;
      const isAmazonProduct = productSource === "amazon" || productSource === "Amazon";
    
      // Prepare product data for translation - match the structure expected by backend
      // Reuse the same pattern as Shopify products but include Amazon-specific fields
      const productDataToTranslate = {
        title: currentProduct?.title || currentProduct?.product_name || "",
        teaser: currentProduct?.teaser || "",
        description: currentProduct?.description || currentProduct?.body_html || "",
        highlights: currentProduct?.highlights || currentProduct?.bullet_points || [],
        ingredients: currentProduct?.ingredients || ingredients || "",
        how_to_use: currentProduct?.how_to_use || howToUse || "",
        dosage: currentProduct?.dosage || dosage || "",
        // Include Amazon-specific fields
        ...(isAmazonProduct && {
          ...(currentProduct?.bullet_points ? { bullet_points: currentProduct.bullet_points } : {}),
          ...(currentProduct?.product_type ? { product_type: currentProduct.product_type } : {}),
          ...(currentProduct?.asin ? { asin: currentProduct.asin } : {}),
          ...(currentProduct?.marketplace_id ? { marketplace_id: currentProduct.marketplace_id } : {})
        }),
        metafields: Array.isArray(currentProduct?.metafields) ? currentProduct.metafields : [],
        // Include all additional sections from accumulatedStreamData (but filter out undefined/null values)
        ...Object.fromEntries(
          Object.entries(accumulatedStreamData || {}).filter(([_, v]) => v !== undefined && v !== null)
        )
      };
      
      // Remove any undefined or null values to ensure clean JSON
      Object.keys(productDataToTranslate).forEach(key => {
        if (productDataToTranslate[key] === undefined || productDataToTranslate[key] === null) {
          delete productDataToTranslate[key];
        }
      });

      // Helper function to process and update translated data
      const processTranslatedChunk = (parsedData) => {
        if (!parsedData || typeof parsedData !== 'object') return;

        // Update product data incrementally
        setProductData(prevData => ({
          ...prevData,
          ...parsedData
        }));

        // Update accumulated stream data
        setAccumulatedStreamData(prevData => ({
          ...prevData,
          ...parsedData
        }));

        // Update individual sections if they exist
        if (parsedData.ingredients) {
          setIngredients(parsedData.ingredients);
        }
        if (parsedData.how_to_use) {
          setHowToUse(parsedData.how_to_use);
        }
        if (parsedData.dosage) {
          setDosage(parsedData.dosage);
        }

        // Force re-render for UI updates
        setRenderKey(prev => prev + 1);
      };

      // Use the translation service
      await translateContent({
        jsonData: productDataToTranslate,
        sourceLanguage: "English",
        targetLanguage: languageValue,
        targetRegion: locationValue || "Global",
        sessionId: activeSessionId,
        onChunk: (chunk, accumulatedData) => {
          // If chunk is a parsed object, process it incrementally
          if (typeof chunk === 'object' && chunk !== null) {
            processTranslatedChunk(chunk);
          }
        },
        onComplete: (finalData) => {
          try {
            // Parse the final translated data
            let parsedTranslation = finalData;
            
            // If it's a string, try to parse it
            if (typeof finalData === 'string') {
              // Try to extract JSON from the string
              const jsonMatch = finalData.match(/\{[\s\S]*\}/);
              if (jsonMatch) {
                parsedTranslation = JSON.parse(jsonMatch[0]);
              } else {
                parsedTranslation = JSON.parse(finalData);
              }
            }
            
            // Process the final translated data
            if (parsedTranslation && typeof parsedTranslation === 'object') {
              processTranslatedChunk(parsedTranslation);
            }
            
            toast.success("Translation completed successfully! Your product content and metafields have been translated.");
          } catch (parseError) {
            console.error('Error parsing final translation data:', parseError);
            toast.warning("Translation completed but some data may not have been parsed correctly.");
          } finally {
            setIsLoading(false);
            setIsStreaming(false);
            setRegeneratingSection([]); // Clear regenerating sections when translation completes
          }
        },
        onError: (error) => {
          console.error("Translation error:", error);
          toast.error("Translation failed: " + (error.message || "Unknown error"));
          setIsLoading(false);
          setIsStreaming(false);
          setRegeneratingSection([]); // Clear regenerating sections on error
        }
      });
      
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Translation failed: " + (error.message || "Unknown error"));
      setIsLoading(false);
      setIsStreaming(false);
      setRegeneratingSection([]); // Clear regenerating sections on error
    }
  };

  const handleOptimize = async (customPrompt = null,isTranslate=false) => {
    console.log("API CALL: handleOptimize in EditProductDescription/index.jsx");
    const sku = productData?.product_sku || productData?.sku || initialSku;
    if (!sku) {
      setError("Please provide an SKU.");
      return;
    }

    const productSource = initialProductData?.source || productData?.source;
    const isShopifyProduct = productSource === "Shopify";
    const isAmazonProduct = productSource === "amazon" || productSource === "Amazon";
    let agentIdToUse = activeAgentId;
    let sessionIdToUse = activeSessionId;

    if (isShopifyProduct || isAmazonProduct) {
      const { agentId: newAgentId, sessionId: newSessionId } = manageAgentSessionIds(true);
      setActiveAgentId(newAgentId);
      setActiveSessionId(newSessionId);
      agentIdToUse = newAgentId;
      sessionIdToUse = newSessionId;
    }

    setIsLoading(true);
    setIsStreaming(true);
    setRegeneratingSection([]); // Clear any previous regenerating sections
    setProductData({});
    setIngredients("");
    setHowToUse("");
    setStreamingLog([]); // Clear previous log
    setAccumulatedStreamData({}); // Clear accumulated data
    let streamedData = {};
    setIsLoading(false); // Hide overlay loader immediately after starting streaming
    const languageValue = language?.label || language || "";
    const locationValue = locationFocus?.label || locationFocus || "";
    
    const promptDetails=
      !isTranslate?details
      :
      `${details} and Translate the entire product description to ${languageValue} for the ${locationValue} market. Do not modify any content or structure — only translate the text faithfully. Do not add, remove, or optimize any sections.`
      
    await generateProductDescriptionStream(
      sku,
      keywords,
      "",
      promptDetails,
      agentIdToUse,
      sessionIdToUse,
      (chunk) => {
        setStreamingLog((prev) => [...prev, chunk]);
        // setLastChunk(chunk);

        // Store all chunk data in accumulatedStreamData, ensuring objects are properly preserved
        if (chunk && typeof chunk === "object") {
          setAccumulatedStreamData((prev) => {
            const newData = { ...prev };

            if (chunk.component_name && chunk.value !== undefined) {
              // Properly store the value, preserving objects
              newData[chunk.component_name] = chunk.value;
            } else if (!chunk.component_name) {
              // If no component_name, merge all keys
              Object.keys(chunk).forEach((key) => {
                newData[key] = chunk[key];
              });
            }

            return newData;
          });
        }

        // ...existing code for updating productData, ingredients, howToUse...
        if (chunk.component_name && chunk.value !== undefined) {
          if (chunk.component_name === "ingredients") {
            if (typeof chunk.value === "string") {
              setIngredients(chunk.value);
            } else if (typeof chunk.value === "object") {
              const paragraph = Object.entries(chunk.value)
                .map(([key, value]) => `${key}: ${value}\n`)
                .join(" ");
              setIngredients(paragraph);
            }
            streamedData["ingredients"] = chunk.value;
          } else if (chunk.component_name === "how_to_use") {
            if (typeof chunk.value === "string") {
              setHowToUse(chunk.value);
            } else if (typeof chunk.value === "object") {
              const paragraph = Object.entries(chunk.value)
                .map(([key, value]) => `${key}: ${value}\n`)
                .join(" ");
              setHowToUse(paragraph);
            }
            streamedData["how_to_use"] = chunk.value;
          } else if (chunk.component_name === "dosage") {
            if (typeof chunk.value === "string") {
              setDosage(chunk.value);
            } else if (typeof chunk.value === "object") {
              const paragraph = Object.entries(chunk.value)
                .map(([key, value]) => `${key}: ${value}\n`)
                .join(" ");
              setDosage(paragraph);
            }
            streamedData["dosage"] = chunk.value;
          } else {
            streamedData[chunk.component_name] = chunk.value;
          }
          setProductData((prev) => ({
            ...prev,
            [chunk.component_name]: chunk.value,
          }));
        }
      },
      () => {
        setIsLoading(false);
        setIsStreaming(false);
        setRegeneratingSection([]); // Clear regenerating sections when streaming completes
        // Merge with existing accumulated streaming data instead of replacing
        setAccumulatedStreamData((prev) => ({
          ...prev,
          ...streamedData,
        }));
      },
      (err) => {
        setError(err.message || "Streaming error");
        setIsLoading(false);
        setIsStreaming(false);
        setRegeneratingSection([]); // Clear regenerating sections on error
      },
      undefined, // previousResponse not needed for optimize
      languageValue,
      locationValue,
      customPrompt
    );
  };

  // Auto-scroll streaming log to bottom
  useEffect(() => {
    if (isStreaming && streamingLogRef.current) {
      streamingLogRef.current.scrollTop = streamingLogRef.current.scrollHeight;
    }
  }, [streamingLog, isStreaming]);

  const onLoadproductIngredient = async (agentId, sessionId) => {
    try {
      const section = "ingredients";
      const response = await generateProductDescription(
        initialSku,
        keywords,
        section,
        "",
        agentId,
        sessionId
      );
      if (response?.data) {
        if (typeof response.data === "string") {
          setIngredients(response.data);
          return;
        } else {
          const paragraph = Object.entries(response?.data)
            .map(([key, value]) => `${key}: ${value}\n`)
            .join(" ");
          setIngredients(paragraph);
        }
      }
    } catch (error) {
      setError(error.message || "Failed to rerieve ingredients");
    }
  };

  const onLoadproductHowToUse = async (agentId, sessionId) => {
    try {
      const section = "how_to_use";
      const response = await generateProductDescription(
        initialSku,
        keywords,
        section,
        "",
        agentId,
        sessionId
      );
      if (response?.data) {
        if (typeof response.data === "string") {
          setHowToUse(response.data);
          return;
        } else {
          const paragraph = Object.entries(response?.data)
            .map(([key, value]) => `${key}: ${value}\n`)
            .join(" ");
          setHowToUse(paragraph);
        }
      }
    } catch (error) {
      setError(error.message || "Failed to rerieve how to use");
    }
  };

  useEffect(() => {
    const { agentId, sessionId } = manageAgentSessionIds();
    setActiveAgentId(agentId);
    setActiveSessionId(sessionId);
   
    if (hasLoadedRef.current) {
      return;
    }
   
    // Priority 1: If we have URL product ID (from page refresh or direct navigation), fetch by ID
    if (urlProductId) {
      const fetchProductDataById = async () => {
        // Check if this is a Shopify or Amazon product by checking initialProductData first
        const isShopifyProduct = initialProductData?.source === "Shopify";
        const isAmazonProduct = initialProductData?.source === "amazon" || initialProductData?.source === "Amazon";
        
        try {
          if ((!isShopifyProduct && !isAmazonProduct) || !initialProductData) {
            setIsLoading(true);
          }
          
          console.log("Fetching product by URL ID:", urlProductId);
          let response;
          let detectedSource = null;
          
          if (isShopifyProduct) {
            response = await fetchShopifyProductById(urlProductId);
            detectedSource = "Shopify";
          } else if (isAmazonProduct) {
            response = await fetchAmazonProductById(urlProductId);
            detectedSource = "amazon";
          } else {
          
            try {
              response = await fetchProductById(urlProductId);
              detectedSource = "local";
              console.log("Fetched local product by URL ID");
            } catch (localError) {
              console.log("Not a local product, trying Amazon product fetch");
              // Try Amazon as fallback (since Amazon products use SKU as ID)
              try {
                response = await fetchAmazonProductById(urlProductId);
                if (response && (response.data || response)) {
                  detectedSource = "amazon";
                  console.log("Detected as Amazon product by URL ID");
                } else {
                  // Amazon fetch returned null (product not found)
                  throw new Error("Product not found");
                }
              } catch (amazonError) {
                console.log("Product not found in local or Amazon");
                throw localError; // Re-throw the original error
              }
            }
          }

          const fetchedProduct = response?.data || response;
          
          if (fetchedProduct) {
            // Add source to product data if detected
            if (detectedSource && !fetchedProduct.source) {
              fetchedProduct.source = detectedSource;
            }
            
            // For Shopify and Amazon products, extract metafields from nested structure
            let productDataToSet = fetchedProduct;
            if ((isShopifyProduct || isAmazonProduct || detectedSource === "amazon") && fetchedProduct.data?.metafields) {
              productDataToSet = {
                ...fetchedProduct,
                metafields: fetchedProduct.data.metafields
              };
            } else if ((isAmazonProduct || detectedSource === "amazon") && fetchedProduct.metafields) {
              // Amazon products might have metafields directly
              productDataToSet = fetchedProduct;
            }

            // For Shopify and Amazon products, merge with initialProductData to preserve list data
            // If no initialProductData, use the fetched product directly
            if ((isShopifyProduct || isAmazonProduct || detectedSource === "amazon") && initialProductData) {
              // Handle comprehensive data structure (data.images, data.metafields, etc.)
              const imageData = isShopifyProduct || (isAmazonProduct || detectedSource === "amazon") && fetchedProduct.data?.images
                ? (fetchedProduct.data?.images || []).map(img => img.src || img)
                : (fetchedProduct.images || []);
              const heroImageData = isShopifyProduct || (isAmazonProduct || detectedSource === "amazon") && fetchedProduct.data?.image
                ? (fetchedProduct.data?.image?.src || fetchedProduct.data?.image)
                : fetchedProduct.image;

              setProductData(prevData => ({
                ...initialProductData, 
                ...productDataToSet,
                 title: initialProductData?.title || productDataToSet.title || fetchedProduct.data?.title,
                description: initialProductData?.description || productDataToSet.description || fetchedProduct.data?.body_html || fetchedProduct.data?.description,
                image: initialProductData?.image || heroImageData,
                images: initialProductData?.images || imageData,
                price: initialProductData?.price || productDataToSet.price || fetchedProduct.data?.price,
                metafields: productDataToSet.metafields || fetchedProduct.data?.metafields || initialProductData?.metafields || [],
                variants: fetchedProduct.data?.variants || productDataToSet.variants || initialProductData?.variants || [],
                collections: fetchedProduct.data?.collections || productDataToSet.collections || initialProductData?.collections || [],
                vendor: fetchedProduct.data?.vendor || productDataToSet.vendor || initialProductData?.vendor,
                brand: fetchedProduct.data?.brand || productDataToSet.brand || initialProductData?.brand
              }));
            } else if ((isAmazonProduct || detectedSource === "amazon") && fetchedProduct.data) {
              // For Amazon products without initialProductData, extract comprehensive data
              const imageData = (fetchedProduct.data?.images || []).map(img => img.src || img);
              const heroImageData = fetchedProduct.data?.image?.src || fetchedProduct.data?.image || (imageData.length > 0 ? imageData[0] : null);
              
              setProductData({
                ...fetchedProduct.data,
                id: fetchedProduct.data?.id || fetchedProduct.id,
                title: fetchedProduct.data?.title || fetchedProduct.title,
                description: fetchedProduct.data?.body_html || fetchedProduct.data?.description || fetchedProduct.description,
                image: heroImageData,
                images: imageData,
                metafields: fetchedProduct.data?.metafields || [],
                variants: fetchedProduct.data?.variants || [],
                collections: fetchedProduct.data?.collections || [],
                source: detectedSource || 'amazon'
              });
            } else {
              // For regular products or when no initialProductData, set all the state with fetched data
              setProductData(productDataToSet);
            }
            setSavedProductId(fetchedProduct.id);

            // If the fetched product has its own agent/session ID, use them
            if (fetchedProduct.agent_id) {
              setActiveAgentId(fetchedProduct.agent_id);
            }
            if (fetchedProduct.session_id) {
              setActiveSessionId(fetchedProduct.session_id);
            }

            // Set form fields from the actual saved product
            if (
              fetchedProduct.keywords &&
              Array.isArray(fetchedProduct.keywords)
            ) {
              setKeywords(fetchedProduct.keywords);
            } else if (
              fetchedProduct.keywords &&
              typeof fetchedProduct.keywords === "string"
            ) {
              setKeywords(
                fetchedProduct.keywords.split(",").map((k) => k.trim())
              );
            }
            setDetails(fetchedProduct.further_details || "");
            setIngredients(fetchedProduct.ingredients || "");
            setHowToUse(fetchedProduct.how_to_use || "");
            
            // Set images - handle Shopify and Amazon comprehensive data structure
            if (isShopifyProduct && fetchedProduct.data?.images) {
              // Shopify images are in data.images array with src property
              setProductImages(cleanImageArray(fetchedProduct.data.images.map(img => img.src || img) || []));
              setHeroImage(cleanImageUrl(fetchedProduct.data.image?.src || fetchedProduct.data.image || ""));
            } else if ((isAmazonProduct || detectedSource === "amazon") && fetchedProduct.data?.images) {
              // Amazon comprehensive data structure (matching Shopify)
              setProductImages(cleanImageArray(fetchedProduct.data.images.map(img => img.src || img) || []));
              setHeroImage(cleanImageUrl(fetchedProduct.data.image?.src || fetchedProduct.data.image || fetchedProduct.image || ""));
            } else if (isAmazonProduct || detectedSource === "amazon") {
              // Amazon products from list (simple structure)
              setProductImages(cleanImageArray(fetchedProduct.images || []));
              setHeroImage(cleanImageUrl(fetchedProduct.image || ""));
            } else if (!isShopifyProduct) {
              // Regular products
              setProductImages(cleanImageArray(fetchedProduct.images || []));
              setHeroImage(cleanImageUrl(fetchedProduct.image || ""));
            }

            if (fetchedProduct.language) {
              setLanguage({
                label: fetchedProduct.language,
                value: fetchedProduct.language,
              });
            }
            if (fetchedProduct.location) {
              setLocationFocus({
                label: fetchedProduct.location,
                value: fetchedProduct.location,
              });
            }

            // Set foray response data
            if (fetchedProduct.foray_response) {
              try {
                const forayData =
                  typeof fetchedProduct.foray_response === "string"
                    ? JSON.parse(fetchedProduct.foray_response)
                    : fetchedProduct.foray_response;

                

                setAccumulatedStreamData(forayData);

                // Extract metafields from foray_response for local products
                if (forayData.metafields && Array.isArray(forayData.metafields)) {
                                   setProductData((prev) => ({
                    ...prev,
                    metafields: forayData.metafields,
                  }));
                }

                // Update productData to include foray_response title if available
                if (forayData.title) {
                  setProductData((prev) => ({
                    ...prev,
                    title: forayData.title,
                  }));
                }
              } catch (parseErr) {
                console.warn("Error parsing foray_response:", parseErr);
              }
            }

            console.log(
              "Successfully loaded product data by URL ID:",
              urlProductId,
              fetchedProduct.title
            );
            hasLoadedRef.current = true;
          }
        } catch (err) {
          console.error("Error fetching product by URL ID:", err);
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchProductDataById();
      return;
    }
    
    // Priority 2: If we have savedProductId (from recent save), fetch by ID to get the actual saved data
    if (savedProductId) {
      const fetchProductDataById = async () => {
        // Check if this is a Shopify or Amazon product
        const isShopifyProduct = initialProductData?.source === "Shopify";
        const isAmazonProduct = initialProductData?.source === "amazon" || initialProductData?.source === "Amazon";
        
        try {
          if ((!isShopifyProduct && !isAmazonProduct) || !initialProductData) {
            setIsLoading(true);
          }
         
          let response;
          
          if (isShopifyProduct) {
            response = await fetchShopifyProductById(savedProductId);
          } else if (isAmazonProduct) {
            response = await fetchAmazonProductById(savedProductId);
          } else {
            
            response = await fetchProductById(savedProductId);
          }

          const fetchedProduct = response?.data || response;
          
          if (fetchedProduct) {
            
                     let productDataToSet = fetchedProduct;
            if (isShopifyProduct && fetchedProduct.data?.metafields) {
              productDataToSet = {
                ...fetchedProduct,
                metafields: fetchedProduct.data.metafields
              };
            }

            // For Shopify and Amazon products, merge with initialProductData to preserve list data
            if (isShopifyProduct || isAmazonProduct) {
              setProductData(prevData => ({
                ...initialProductData, 
                ...productDataToSet,
                                title: initialProductData?.title || productDataToSet.title,
                description: initialProductData?.description || productDataToSet.description,
                image: initialProductData?.image || productDataToSet.image,
                images: initialProductData?.images || productDataToSet.images,
                price: initialProductData?.price || productDataToSet.price,
                              metafields: productDataToSet.metafields || initialProductData?.metafields || []
              }));
            } else {
             
              setProductData(productDataToSet);
            }

            // If the fetched product has its own agent/session ID, use them
            if (fetchedProduct.agent_id) {
              setActiveAgentId(fetchedProduct.agent_id);
            }
            if (fetchedProduct.session_id) {
              setActiveSessionId(fetchedProduct.session_id);
            }

            // Set form fields from the actual saved product
            if (
              fetchedProduct.keywords &&
              Array.isArray(fetchedProduct.keywords)
            ) {
              setKeywords(fetchedProduct.keywords);
            } else if (
              fetchedProduct.keywords &&
              typeof fetchedProduct.keywords === "string"
            ) {
              setKeywords(
                fetchedProduct.keywords.split(",").map((k) => k.trim())
              );
            }
            setDetails(fetchedProduct.further_details || "");
            setIngredients(fetchedProduct.ingredients || "");
            setHowToUse(fetchedProduct.how_to_use || "");

         
            if (!isShopifyProduct) {
              if (fetchedProduct.images) {
                setProductImages(cleanImageArray(fetchedProduct.images));
              }
              if (fetchedProduct.image) {
                setHeroImage(cleanImageUrl(fetchedProduct.image));
              }
            }

            if (fetchedProduct.language) {
              setLanguage({
                label: fetchedProduct.language,
                value: fetchedProduct.language,
              });
            }
            if (fetchedProduct.location) {
              setLocationFocus({
                label: fetchedProduct.location,
                value: fetchedProduct.location,
              });
            }

            // Set foray response data
            if (fetchedProduct.foray_response) {
              try {
                const forayData =
                  typeof fetchedProduct.foray_response === "string"
                    ? JSON.parse(fetchedProduct.foray_response)
                    : fetchedProduct.foray_response;

                

                setAccumulatedStreamData(forayData);

                // Extract metafields from foray_response for local products
                if (forayData.metafields && Array.isArray(forayData.metafields)) {
                  console.log("Found metafields in foray_response:", forayData.metafields.length);
                  setProductData((prev) => ({
                    ...prev,
                    metafields: forayData.metafields,
                  }));
                }

                // Update productData to include foray_response title if available
                if (forayData.title) {
                  setProductData((prev) => ({
                    ...prev,
                    title: forayData.title,
                  }));
                }
              } catch (parseErr) {
                console.warn("Error parsing foray_response:", parseErr);
              }
            }

            console.log(
              "Successfully loaded product data by ID:",
              savedProductId,
              fetchedProduct.title
            );
            hasLoadedRef.current = true;
          }
        } catch (err) {
          console.error("Error fetching product by ID:", err);
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchProductDataById();
      return;
    }

    // Priority 2: If we have initialProductData, use it
    if (initialProductData) {
   
      // Check if this is a Shopify or Amazon product that needs to be fetched
      const isShopifyProduct = initialProductData.source === "Shopify";
      const isAmazonProduct = initialProductData.source === "amazon" || initialProductData.source === "Amazon";
      
      if ((isShopifyProduct || isAmazonProduct) && initialProductData.id) {
        if (hasLoadedRef.current) {
          return;
        }
        
        const fetchExternalProductData = async () => {
          try {
            let response;
            if (isShopifyProduct) {
              response = await fetchShopifyProductById(initialProductData.id);
            } else if (isAmazonProduct) {
              response = await fetchAmazonProductById(initialProductData.id);
            }
            
            if (response && response.data) {
              const fetchedProduct = response.data;
             
              // Extract metafields from the nested structure and flatten the product data (for Shopify and Amazon)
              let productDataWithMetafields = fetchedProduct;
              if ((isShopifyProduct || isAmazonProduct) && fetchedProduct.data?.metafields) {
                productDataWithMetafields = {
                  ...fetchedProduct,
                  metafields: fetchedProduct.data?.metafields || []
                };
              }
              
              // Helper function to parse rich text metafields
              const parseRichText = (value) => {
                if (!value || typeof value !== 'string') return '';
                try {
                  const parsed = JSON.parse(value);
                  if (parsed.children && Array.isArray(parsed.children)) {
                    return parsed.children
                      .map(child => child.children?.map(c => c.value).join('') || '')
                      .join('');
                  }
                } catch (e) {
                  return value;
                }
                return value;
              };
              
              // Extract data from metafields (for both Shopify and Amazon)
              const metafields = productDataWithMetafields.metafields || fetchedProduct.data?.metafields || [];
              if (metafields && Array.isArray(metafields)) {
                // Extract ingredients from metafields
                const ingredientsMetafield = metafields.find(m => m.key === 'ingredients');
                if (ingredientsMetafield && ingredientsMetafield.value) {
                  productDataWithMetafields.ingredients = parseRichText(ingredientsMetafield.value);
                }
                
                // Extract brand from metafields
                const brandMetafield = metafields.find(m => m.key === 'brand');
                if (brandMetafield && brandMetafield.value) {
                  productDataWithMetafields.brand = brandMetafield.value;
                }
                
                // Extract manufacturer from metafields
                const manufacturerMetafield = metafields.find(m => m.key === 'manufacturer');
                if (manufacturerMetafield && manufacturerMetafield.value) {
                  productDataWithMetafields.manufacturer = manufacturerMetafield.value;
                }
                
                // Extract shelf_life from metafields
                const shelfLifeMetafield = metafields.find(m => m.key === 'shelf_life');
                if (shelfLifeMetafield && shelfLifeMetafield.value) {
                  productDataWithMetafields.shelf_life = shelfLifeMetafield.value;
                }
                
                // Extract manufacturer_address from metafields
                const manufacturerAddressMetafield = metafields.find(m => m.key === 'manufacturer_address1' || m.key === 'manufacturer_address');
                if (manufacturerAddressMetafield && manufacturerAddressMetafield.value) {
                  productDataWithMetafields.manufacturer_address = manufacturerAddressMetafield.value;
                }
                
                // Extract fssai_license from metafields
                const fssaiMetafield = metafields.find(m => m.key === 'fssai_license');
                if (fssaiMetafield && fssaiMetafield.value) {
                  productDataWithMetafields.fssai_license = fssaiMetafield.value;
                }
                
                // Extract pack_type from metafields
                const packTypeMetafield = metafields.find(m => m.key === 'pack_type');
                if (packTypeMetafield && packTypeMetafield.value) {
                  productDataWithMetafields.pack_type = packTypeMetafield.value;
                }
                
                // Extract product_dimensions from metafields
                const productDimensionMetafield = metafields.find(m => m.key === 'product_dimension' || m.key === 'product_dimensions');
                if (productDimensionMetafield && productDimensionMetafield.value) {
                  productDataWithMetafields.product_dimensions = productDimensionMetafield.value;
                }
                
                // Extract weight from metafields
                const weightMetafield = metafields.find(m => m.key === 'weight');
                if (weightMetafield && weightMetafield.value) {
                  productDataWithMetafields.weight = weightMetafield.value;
                }
              }
            
              // Handle images for comprehensive data structure
              const imageData = (isShopifyProduct || isAmazonProduct) && fetchedProduct.data?.images
                ? fetchedProduct.data.images.map(img => img.src || img)
                : (fetchedProduct.images || []);
              const heroImageData = (isShopifyProduct || isAmazonProduct) && fetchedProduct.data?.image
                ? (fetchedProduct.data.image?.src || fetchedProduct.data.image)
                : fetchedProduct.image;

              // Merge with initialProductData to preserve list data
              setProductData(prevData => ({
                ...initialProductData, // Start with the original list data
                ...productDataWithMetafields, // Override with fresh Shopify/Amazon data where available
                title: initialProductData?.title || productDataWithMetafields.title || fetchedProduct.data?.title,
                description: initialProductData?.description || productDataWithMetafields.description || fetchedProduct.data?.body_html || fetchedProduct.data?.description,
                image: initialProductData?.image || heroImageData,
                images: initialProductData?.images || imageData,
                price: initialProductData?.price || productDataWithMetafields.price,
                // Ensure metafields are properly set
                metafields: productDataWithMetafields.metafields || fetchedProduct.data?.metafields || initialProductData?.metafields || [],
                variants: fetchedProduct.data?.variants || productDataWithMetafields.variants || initialProductData?.variants || [],
                collections: fetchedProduct.data?.collections || productDataWithMetafields.collections || initialProductData?.collections || [],
                // Include extracted fields from metafields
                ingredients: productDataWithMetafields.ingredients || initialProductData?.ingredients,
                brand: productDataWithMetafields.brand || initialProductData?.brand,
                manufacturer: productDataWithMetafields.manufacturer || initialProductData?.manufacturer,
                shelf_life: productDataWithMetafields.shelf_life || initialProductData?.shelf_life,
                manufacturer_address: productDataWithMetafields.manufacturer_address || initialProductData?.manufacturer_address,
                fssai_license: productDataWithMetafields.fssai_license || initialProductData?.fssai_license,
                pack_type: productDataWithMetafields.pack_type || initialProductData?.pack_type,
                product_dimensions: productDataWithMetafields.product_dimensions || initialProductData?.product_dimensions,
                weight: productDataWithMetafields.weight || initialProductData?.weight
              }));
              hasLoadedRef.current = true;
              setSavedProductId(fetchedProduct.id);
              
              // If the fetched product has its own agent/session ID, use them
              if (fetchedProduct.agent_id) {
                setActiveAgentId(fetchedProduct.agent_id);
              }
              if (fetchedProduct.session_id) {
                setActiveSessionId(fetchedProduct.session_id);
              }

              // Set form fields from the actual saved product
              if (
                productDataWithMetafields.keywords &&
                Array.isArray(productDataWithMetafields.keywords)
              ) {
                setKeywords(productDataWithMetafields.keywords);
              } else if (
                productDataWithMetafields.keywords &&
                typeof productDataWithMetafields.keywords === "string"
              ) {
                setKeywords(
                  productDataWithMetafields.keywords.split(",").map((k) => k.trim())
                );
              }
              setDetails(productDataWithMetafields.further_details || "");
              setIngredients(productDataWithMetafields.ingredients || "");
              setHowToUse(productDataWithMetafields.how_to_use || "");
              // Set image states with cleaned URLs for Shopify and Amazon comprehensive data
              if ((isShopifyProduct || isAmazonProduct) && productDataWithMetafields.data?.images) {
                setProductImages(cleanImageArray(productDataWithMetafields.data.images.map(img => img.src || img) || []));
                setHeroImage(cleanImageUrl(productDataWithMetafields.data.image?.src || productDataWithMetafields.data.image || ""));
              } else if (isAmazonProduct) {
                // For Amazon products from list (simple structure), images are already in the correct format
                setProductImages(cleanImageArray(productDataWithMetafields.images || []));
                setHeroImage(cleanImageUrl(productDataWithMetafields.image || ""));
              }

              if (productDataWithMetafields.language) {
                setLanguage({
                  label: productDataWithMetafields.language,
                  value: productDataWithMetafields.language,
                });
              }
              if (productDataWithMetafields.location) {
                setLocationFocus({
                  label: productDataWithMetafields.location,
                  value: productDataWithMetafields.location,
                });
              }

              // Set foray response data
              if (productDataWithMetafields.foray_response) {
                try {
                  const forayData =
                    typeof productDataWithMetafields.foray_response === "string"
                      ? JSON.parse(productDataWithMetafields.foray_response)
                      : productDataWithMetafields.foray_response;

                  

                  setAccumulatedStreamData(forayData);

                  // Extract metafields from foray_response for local products
                  if (forayData.metafields && Array.isArray(forayData.metafields)) {
                    console.log("Found metafields in foray_response:", forayData.metafields.length);
                    setProductData((prev) => ({
                      ...prev,
                      metafields: forayData.metafields,
                    }));
                  }

                  // Update productData to include foray_response title if available
                  if (forayData.title) {
                    setProductData((prev) => ({
                      ...prev,
                      title: forayData.title,
                    }));
                  }
                } catch (parseErr) {
                  console.warn("Error parsing foray_response:", parseErr);
                }
              }

              console.log(
                "Successfully loaded Shopify product data for initialProductData:",
                initialProductData.id,
                productDataWithMetafields.title
              );
            } else {
              setProductData(initialProductData);
              hasLoadedRef.current = true;
            }
          } catch (err) {
            console.error(`Error fetching ${isShopifyProduct ? 'Shopify' : 'Amazon'} product data:`, err);
            setProductData(initialProductData);
            hasLoadedRef.current = true;
          } finally {
            setIsLoading(false);
          }
        };
        
        fetchExternalProductData();

        return;
      } else {
        // Regular product or no ID, use initialProductData as is
        setProductData(initialProductData);

        // If initialProductData has foray_response, use it
        if (initialProductData.foray_response) {
          try {
            const forayData =
              typeof initialProductData.foray_response === "string"
                ? JSON.parse(initialProductData.foray_response)
                : initialProductData.foray_response;


            setAccumulatedStreamData(forayData);

            // Extract metafields from foray_response for local products
            if (forayData.metafields && Array.isArray(forayData.metafields)) {
              console.log("Found metafields in initial foray_response:", forayData.metafields.length);
              setProductData((prev) => ({
                ...prev,
                metafields: forayData.metafields,
              }));
            }

            // Update productData to include foray_response title if available
            if (forayData.title) {
              setProductData((prev) => ({
                ...prev,
                title: forayData.title,
              }));
            }
          } catch (parseErr) {
            console.warn("Error parsing initial foray_response:", parseErr);
          }
        }
        setIsLoading(false);
        return; // Exit early
      }
    }

    // Priority 3: If we only have SKU, fetch by SKU
    if (initialSku) {
      const fetchProductData = async () => {
        // Check if this is a Shopify or Amazon product
        const isShopifyProduct = initialProductData?.source === "Shopify";
        const isAmazonProduct = initialProductData?.source === "amazon" || initialProductData?.source === "Amazon";
        
        try {
          if ((!isShopifyProduct && !isAmazonProduct) || !initialProductData) {
            setIsLoading(true);
          }
          
          console.log("Fetching product by SKU:", initialSku);
          let response;
          
          if (isShopifyProduct) {
            console.log("Shopify product detected, using Shopify API for SKU:", initialSku);
            // For Shopify products with SKU, we need to use the product ID instead
            const productId = initialProductData?.shopify_id || initialProductData?.id;
            if (productId) {
              response = await fetchShopifyProductById(productId);
            } else {
              throw new Error("Shopify product ID not found");
            }
          } else if (isAmazonProduct) {
            console.log("Amazon product detected, using Amazon API for SKU:", initialSku);
            // For Amazon products, the SKU is the ID
            response = await fetchAmazonProductBySku(initialSku);
          } else {
            console.log("Regular product detected, using regular API for SKU:", initialSku);
            response = await fetchProductBySku(initialSku);
          }

          if (response && response.data) {
            const fetchedProduct = response.data;
            
            // For Shopify products, extract metafields from nested structure
            let productDataToSet = fetchedProduct;
            if (isShopifyProduct && fetchedProduct.data?.metafields) {
              productDataToSet = {
                ...fetchedProduct,
                metafields: fetchedProduct.data.metafields
              };
            }

            // For Shopify and Amazon products, merge with initialProductData to preserve list data
            if (isShopifyProduct || isAmazonProduct) {
              setProductData(prevData => ({
                ...initialProductData, // Start with the original list data
                ...productDataToSet, // Override with fresh Shopify/Amazon data where available
                // Explicitly preserve important fields from initial data
                title: initialProductData?.title || productDataToSet.title,
                description: initialProductData?.description || productDataToSet.description,
                image: initialProductData?.image || productDataToSet.image,
                images: initialProductData?.images || productDataToSet.images,
                price: initialProductData?.price || productDataToSet.price,
                // Ensure metafields are properly set
                metafields: productDataToSet.metafields || initialProductData?.metafields || []
              }));
            } else {
              // For regular products, set all the state with fetched data
              setProductData(productDataToSet);
            }
            setSavedProductId(fetchedProduct.id);

            // Set form fields
            if (
              fetchedProduct.keywords &&
              Array.isArray(fetchedProduct.keywords)
            ) {
              setKeywords(fetchedProduct.keywords);
            } else if (
              fetchedProduct.keywords &&
              typeof fetchedProduct.keywords === "string"
            ) {
              setKeywords(
                fetchedProduct.keywords.split(",").map((k) => k.trim())
              );
            }
            setDetails(fetchedProduct.further_details || "");
            setIngredients(fetchedProduct.ingredients || "");
            setHowToUse(fetchedProduct.how_to_use || "");

            if (fetchedProduct.language) {
              setLanguage({
                label: fetchedProduct.language,
                value: fetchedProduct.language,
              });
            }
            if (fetchedProduct.location) {
              setLocationFocus({
                label: fetchedProduct.location,
                value: fetchedProduct.location,
              });
            }

            // Set images (only for non-Shopify products)
            if (!isShopifyProduct) {
              if (fetchedProduct.images) {
                setProductImages(cleanImageArray(fetchedProduct.images));
              }
              if (fetchedProduct.image) {
                setHeroImage(cleanImageUrl(fetchedProduct.image));
              }
            }

            // Set foray response data
            if (fetchedProduct.foray_response) {
              try {
                const forayData =
                  typeof fetchedProduct.foray_response === "string"
                    ? JSON.parse(fetchedProduct.foray_response)
                    : fetchedProduct.foray_response;

                setAccumulatedStreamData(forayData);

                // Extract metafields from foray_response for local products
                if (forayData.metafields && Array.isArray(forayData.metafields)) {
                  console.log("Found metafields in foray_response:", forayData.metafields.length);
                  setProductData((prev) => ({
                    ...prev,
                    metafields: forayData.metafields,
                  }));
                }

                // Update productData to include foray_response title if available
                if (forayData.title) {
                  setProductData((prev) => ({
                    ...prev,
                    title: forayData.title,
                  }));
                }
              } catch (parseErr) {
                console.warn("Error parsing foray_response:", parseErr);
              }
            }

            console.log(
              "Successfully loaded product data by SKU:",
              initialSku,
              fetchedProduct.title
            );
            hasLoadedRef.current = true;
          }
        } catch (err) {
          console.error("Error fetching product by SKU:", err);
          if (err.message !== "Product not found") {
            setError(err.message);
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchProductData();
      return;
    }

    // If we have no data to load, just finish loading
    setIsLoading(false);
  }, [initialProductData, initialSku, savedProductId, urlProductId]);

  const handleSaveAsDraft = async () => {
    // Check if this is a Shopify or Amazon product that should trigger the name change modal
    // Check multiple sources for the source field
    const productSource =
      initialProductData?.source ||
      productData?.source ||
      (initialProductData?.vendor ? "Shopify" : null);

    const isShopifyProduct = productSource === "Shopify";
    const isAmazonProduct = productSource === "amazon" || productSource === "Amazon";

    console.log(
      "Save as draft clicked - Product source:",
      productSource,
      "Is Shopify:",
      isShopifyProduct,
      "Is Amazon:",
      isAmazonProduct
    );
    console.log("Initial product data:", initialProductData);
    console.log("Current product data:", productData);

    if (isShopifyProduct || isAmazonProduct) {
      console.log(`Showing name change modal for ${isShopifyProduct ? 'Shopify' : 'Amazon'} product`);
      setShowNameChangeModal(true);
      return;
    }

    // For local products, show the local draft modal
    console.log("Showing local draft modal for local product");
    setShowLocalDraftModal(true);
  };

  const handleNameChangeConfirm = async (newName) => {
    console.log("Creating new draft with name:", newName);

    // Update the product title and clear the existing product ID to force creation of new record
    setProductData((prev) => ({ ...prev, title: newName }));
    setSavedProductId(null); // Force creation of new record

    // Show loading state
    setIsLoading(true);

    try {
      // Perform the save with the new name
      await performSaveAsDraft(newName, false);
      toast.success(`New draft "${newName}" created successfully!`);
    } catch (error) {
      toast.error("Failed to create new draft. Please try again.");
      console.error("Error creating new draft:", error);
    }
  };

  const handleLocalDraftConfirm = async (draftChoice, newDraftTitle) => {
    console.log("Local draft modal confirmed:", draftChoice, newDraftTitle);
    
    if (draftChoice === "update") {
      // Update existing draft
      await performSaveAsDraft(null, true);
    } else if (draftChoice === "new") {
      // Create new draft
      console.log("Creating new local draft with title:", newDraftTitle);
      
      // Update the product title and clear the existing product ID to force creation of new record
      setProductData((prev) => ({ ...prev, title: newDraftTitle }));
      setSavedProductId(null); // Force creation of new record
      
      // Show loading state
      setIsLoading(true);
      
      try {
        // Perform the save with the new title and force new record creation
        await performSaveAsDraft(newDraftTitle, false, true); // Added third parameter to force new record
        toast.success(`New draft "${newDraftTitle}" created successfully!`);
      } catch (error) {
        toast.error("Failed to create new draft. Please try again.");
        console.error("Error creating new draft:", error);
      }
    }
    
    // Close the modal
    setShowLocalDraftModal(false);
  };

  const performSaveAsDraft = async (
    overrideTitle = null,
    showSuccessToast = true,
    forceNewRecord = false
  ) => {
    setIsLoading(true);
    setError(null);

    const currentMetafields = productData && 'metafields' in productData 
      ? (productData.metafields || []) 
      : (initialProductData?.metafields || []);
    
    let latestTitle = productData?.title || "";
    
    if (Array.isArray(currentMetafields) && currentMetafields.length > 0) {
      const titleMetafields = currentMetafields.filter(m => 
        m && m.key && m.key.toLowerCase().includes('title') && m.value && m.value.trim()
      );
      if (titleMetafields.length > 0) {
        latestTitle = titleMetafields[0]?.value || latestTitle;
      }
    }
    
    if (overrideTitle) {
      latestTitle = overrideTitle;
    }

    // Use the accumulated streaming data if available, otherwise fall back to current state
    const forayResponseData =
      Object.keys(accumulatedStreamData).length > 0
        ? {
            // Always include the base product data first - use latest title from metafields
            title: latestTitle,
            subtitle: productData?.subtitle || "",
            teaser: productData?.teaser || "",
            description: productData?.description || "",
            highlights: productData?.highlights || [],
            offer_title: productData?.offer_title || "",
            offer_description: productData?.offer_description || "",
            featured_title: productData?.featured_title || "",
            features: productData?.features || [],
            ingredients: ingredients,
            how_to_use: howToUse,
            dosage: dosage,
            about: productData?.about || "",
            // Include metafields in foray_response - use current metafields if productData exists, otherwise fall back
            metafields: productData && 'metafields' in productData 
              ? (productData.metafields || []) 
              : (initialProductData?.metafields || []),
            // Then merge with any additional sections from accumulatedStreamData
            // but filter out [object Object] strings and default placeholder content
            ...Object.keys(accumulatedStreamData).reduce((acc, key) => {
              const value = accumulatedStreamData[key];

              // Skip if it's an [object Object] string
              if (typeof value === "string" && value === "[object Object]") {
                return acc;
              }

              // Log what we're including
              console.log(`Including ${key} with value:`, value);

              // Skip the old main benefits array to avoid confusion
              if (key === "benefits") {
                return acc;
              }
              
              // Skip metafields from accumulatedStreamData - we want to use the current productData.metafields
              if (key === "metafields") {
                return acc;
              }
              
              // Skip title from accumulatedStreamData - we want to use the latest title from metafields or productData
              if (key === "title") {
                return acc;
              }
              
              if (value !== undefined && value !== null && value !== "") {
                acc[key] = value;
              }

              return acc;
            }, {}),
          }
        : {
            title: latestTitle,
            subtitle: productData?.subtitle || "",
            teaser: productData?.teaser || "",
            description: productData?.description || "",
            highlights: productData?.highlights || [],
            offer_title: productData?.offer_title || "",
            offer_description: productData?.offer_description || "",
            featured_title: productData?.featured_title || "",
            features: productData?.features || [],
            ingredients: ingredients,
            how_to_use: howToUse,
            dosage: dosage,
            about: productData?.about || "",
            collections: productData?.collections || initialProductData?.collections || [],
            variants: productData?.variants || initialProductData?.variants || [],
            // Include metafields in foray_response - use current metafields if productData exists, otherwise fall back
            metafields: productData && 'metafields' in productData 
              ? (productData.metafields || []) 
              : (initialProductData?.metafields || []),
            // Remove benefits array from fallback too
          };

    // Check if we have section benefits - include both 'benefit' and 'benefits' patterns
    const sectionBenefitKeys = Object.keys(forayResponseData).filter(
      (key) => key === "benefit" || key.startsWith("benefits")
    );

    // Check if we have any valid benefit content
    const hasValidBenefitContent = Object.keys(forayResponseData).some(
      (key) => {
        if (key === "benefit" || key.startsWith("benefits")) {
          const value = forayResponseData[key];
          return (
            value &&
            typeof value === "object" &&
            value.headline &&
            value.benefits &&
            Array.isArray(value.benefits) &&
            value.benefits.length > 0 &&
            !value.benefits.every(
              (benefit) =>
                benefit.title &&
                benefit.description &&
                (benefit.title.includes("Benefit 1") ||
                  benefit.title.includes("Benefit 2") ||
                  benefit.description === "Generated benefit description")
            )
          );
        }
        return false;
      }
    );


    // Count total benefits across all sections - include both 'benefit' and 'benefits' patterns
    const totalSectionBenefits = Object.keys(forayResponseData)
      .filter((key) => key === "benefit" || key.startsWith("benefits"))
      .reduce((total, key) => {
        const section = forayResponseData[key];
        return (
          total + (section && section.benefits ? section.benefits.length : 0)
        );
      }, 0);

    let finalLatestTitle = latestTitle;
    const finalCurrentMetafields = productData && 'metafields' in productData 
      ? (productData.metafields || []) 
      : (currentMetafields || []);
    
    if (Array.isArray(finalCurrentMetafields) && finalCurrentMetafields.length > 0) {
      const titleMetafields = finalCurrentMetafields.filter(m => 
        m && m.key && m.key.toLowerCase().includes('title') && m.value && m.value.trim()
      );
      if (titleMetafields.length > 0) {
        const customTitleMetafield = titleMetafields.find(m => 
          m.key && m.key.toLowerCase().includes('custom') && m.key.toLowerCase().includes('title')
        );
        if (customTitleMetafield) {
          finalLatestTitle = customTitleMetafield.value;
        } else {
          finalLatestTitle = titleMetafields[0]?.value || finalLatestTitle;
        }
      }
    }
    
    if (productData?.title && productData.title.trim() && productData.title !== finalLatestTitle) {
      finalLatestTitle = productData.title;
    }
    
    if (overrideTitle) {
      finalLatestTitle = overrideTitle;
    }
    
    const productTitle = overrideTitle || finalLatestTitle || productData?.title || "";
    const productSku = productData?.product_sku || productData?.sku || initialSku || "";
    
    // Validate required fields
    if (!productTitle.trim()) {
      setError("Product title is required. Please wait for the product data to load or provide a title.");
      setIsLoading(false);
      return;
    }
    
    if (!productSku.trim()) {
      setError("Product SKU is required. Please wait for the product data to load or provide a SKU.");
      setIsLoading(false);
      return;
    }
    
    const { title: oldTitle, ...forayResponseDataWithoutTitle } = forayResponseData;
    const finalForayResponseData = {
      ...forayResponseDataWithoutTitle,
      title: finalLatestTitle || latestTitle || productData?.title || "",
      metafields: finalCurrentMetafields,
      collections: productData?.collections || forayResponseData.collections || initialProductData?.collections || [],
      variants: productData?.variants || forayResponseData.variants || initialProductData?.variants || [],
    };
    
    const stringifiedForayResponse = JSON.stringify(finalForayResponseData);
    
    let productPayload = {
      title: productTitle,
      location: locationFocus?.label || locationFocus || "",
      language: language?.label || language || "",
      product_sku: productSku,
      further_details: details,
      status: "draft",
      keywords: Array.isArray(keywords) ? keywords.join(",") : keywords || "",
      foray_response: stringifiedForayResponse, // metafields are now included in foray_response
      product_shopify_id:
        productData?.shopify_id || initialProductData?.shopify_id || "",
      session_id: activeSessionId,
      agent_id: activeAgentId,
      images: productImages || [],
      image: heroImage || "",
      image_upload: productData?.image_upload || [],
      image_names: productData?.image_names || [],
    };

    // If this is for a Shopify or Amazon product, ensure we create a new record by clearing identifiers
    const productSource =
      initialProductData?.source ||
      productData?.source ||
      (initialProductData?.vendor ? "Shopify" : null);

    const isShopifyProduct = productSource === "Shopify";
    const isAmazonProduct = productSource === "amazon" || productSource === "Amazon";

    if ((isShopifyProduct || isAmazonProduct) && overrideTitle) {
      // Clear any existing IDs to ensure we create a new record
      productPayload.product_shopify_id = "";
      // Don't use existing product ID for Shopify/Amazon products
      setSavedProductId(null);
    }
    
    // If forcing new record creation, clear all identifiers
    if (forceNewRecord) {
      console.log("Force new record mode - clearing all product identifiers");
      productPayload.product_shopify_id = "";
      setSavedProductId(null);
      // Also clear any initial product ID that might be used for lookups
      if (initialProductData && initialProductData.id) {
        console.log("Clearing initial product ID for new record creation");
        // We'll need to temporarily clear this from the lookup logic
      }
    }

    try {
      let response;
      let existingProductId = savedProductId;

      // For Shopify/Amazon products with name override, always create new record
      if ((isShopifyProduct || isAmazonProduct) && overrideTitle) {
        existingProductId = null;
      }
      // If forceNewRecord is true, skip all existing product lookups
      else if (forceNewRecord) {
        existingProductId = null;
        console.log("Force new record mode - skipping existing product lookup");
      }
      // Multiple strategies to find existing product (only for non-Shopify/Amazon products)
      else if (!existingProductId && !isShopifyProduct && !isAmazonProduct) {
        // Strategy 1: Check if we have initialProductData with an ID (editing existing product)
        // Skip this if we're forcing a new record
        if (initialProductData && initialProductData.id && !forceNewRecord) {
          existingProductId = initialProductData.id;
          setSavedProductId(existingProductId);
        }
        // Strategy 2: Use the robust findExistingProduct function
        else {
          try {
            const existingProduct = await findExistingProduct(
              initialSku,
              productPayload.title
            );
            if (
              existingProduct &&
              existingProduct.exists &&
              existingProduct.product_id
            ) {
              existingProductId = existingProduct.product_id;
              setSavedProductId(existingProductId);
            }
          } catch (robustCheckError) {
            // Fallback to individual checks
            if (initialSku) {
              try {
                const existingProductBySku = await checkProductExistsBySku(
                  initialSku
                );
                if (
                  existingProductBySku &&
                  existingProductBySku.exists &&
                  existingProductBySku.product_id
                ) {
                  existingProductId = existingProductBySku.product_id;
                  setSavedProductId(existingProductId);
                }
              } catch (skuCheckError) {
                // Final fallback: Check by title
                if (productPayload.title) {
                  try {
                    const existingProductByTitle = await checkProductExists(
                      productPayload.title
                    );
                    if (
                      existingProductByTitle &&
                      existingProductByTitle.exists &&
                      existingProductByTitle.product_id
                    ) {
                      existingProductId = existingProductByTitle.product_id;
                      setSavedProductId(existingProductId);
                    }
                  } catch (titleCheckError) {
                    // Handle error silently
                  }
                }
              }
            }
          }
        }
      }

      // Final decision: update vs create
      console.log("Final decision - existingProductId:", existingProductId, "forceNewRecord:", forceNewRecord);
      if (existingProductId && !forceNewRecord) {
        try {
          // Use the safer update method that handles title conflicts
          response = await updateProductSafely(
            existingProductId,
            productPayload,
            initialProductData
          );

          // Update the product data state to reflect any title changes
          if (overrideTitle) {
            setProductData((prev) => ({
              ...prev,
              title: overrideTitle,
              id: existingProductId,
            }));
            console.log("Updated product data with new title:", overrideTitle);
          }

          // Update browser history state with product ID
          updateLocationState(existingProductId);
          if (showSuccessToast) {
            toast.success("Product draft updated successfully!");
          }
          
          // After successful update, refresh product data to get the latest saved data including collections and variants
          try {
            const refreshResponse = await fetchProductById(existingProductId);
            
            const refreshedProduct = refreshResponse?.data || refreshResponse;
            
            if (refreshedProduct) {
              
              // Parse foray_response if it exists
              if (refreshedProduct.foray_response) {
                try {
                  const parsedForay = typeof refreshedProduct.foray_response === 'string' 
                    ? JSON.parse(refreshedProduct.foray_response)
                    : refreshedProduct.foray_response;
                  
                  // Update productData with refreshed data including collections and variants
                  setProductData({
                    ...refreshedProduct,
                    ...parsedForay,
                    metafields: parsedForay.metafields || refreshedProduct.metafields || [],
                    collections: parsedForay.collections || refreshedProduct.collections || [],
                    variants: parsedForay.variants || refreshedProduct.variants || [],
                    foray_response: parsedForay,
                  });
                } catch (parseError) {
                  console.error("Failed to parse refreshed foray_response:", parseError);
                  setProductData(refreshedProduct);
                }
              } else {
                setProductData(refreshedProduct);
              }
            }
          } catch (refreshError) {
            console.error("Failed to refresh product data after update:", refreshError);
            // Don't throw - the update was successful, just the refresh failed
          }
        } catch (updateError) {
          // Check if it's still a title conflict
          const errorMessages = updateError.response?.data?.errors;
          const isTitleConflict =
            errorMessages &&
            ((Array.isArray(errorMessages) &&
              errorMessages.some((msg) =>
                msg.includes("already been taken")
              )) ||
              (typeof errorMessages === "object" &&
                Object.values(errorMessages).some(
                  (msg) =>
                    typeof msg === "string" &&
                    msg.includes("already been taken")
                )));

          if (isTitleConflict) {
            try {
              response = await updateProductMinimal(
                existingProductId,
                productPayload
              );

              // Update the product data state to reflect any title changes
              if (overrideTitle) {
                setProductData((prev) => ({
                  ...prev,
                  title: overrideTitle,
                  id: existingProductId,
                }));
                console.log(
                  "Updated product data with new title:",
                  overrideTitle
                );
              }

              // Update browser history state with product ID
              updateLocationState(existingProductId);
              if (showSuccessToast) {
                toast.success("Product draft updated successfully!");
              }
            } catch (minimalUpdateError) {
              throw minimalUpdateError;
            }
          } else {
            // If it's not a title conflict, try the regular update without title anyway
            const payloadWithoutTitle = { ...productPayload };
            delete payloadWithoutTitle.title;

            try {
              response = await updateProduct(
                existingProductId,
                payloadWithoutTitle
              );

              // Update the product data state to reflect any title changes
              if (overrideTitle) {
                setProductData((prev) => ({
                  ...prev,
                  title: overrideTitle,
                  id: existingProductId,
                }));
                console.log(
                  "Updated product data with new title:",
                  overrideTitle
                );
              }

              // Update browser history state with product ID
              updateLocationState(existingProductId);
              if (showSuccessToast) {
                toast.success("Product draft updated successfully!");
              }
            } catch (finalUpdateError) {
              throw finalUpdateError;
            }
          }
        }
      } else {
        // As a final safety measure, try to make the title unique if we're creating
        if (productPayload.title) {
          const timestamp = new Date().getTime();
          const originalTitle = productPayload.title;

          // Try the original title first, then add timestamp if needed
          let attempts = 0;
          let titleToTry = originalTitle;

          while (attempts < 3) {
            try {
              if (attempts > 0) {
                titleToTry = `${originalTitle} - ${timestamp}`;
                productPayload.title = titleToTry;
              }

              response = await productSaveAsDraft(productPayload);

              if (response.data && response.data.id) {
                setSavedProductId(response.data.id);
                console.log("New product created with ID:", response.data.id);

                // Update the product data state to reflect the new title
                if (overrideTitle && titleToTry) {
                  setProductData((prev) => ({
                    ...prev,
                    title: titleToTry,
                    id: response.data.id,
                  }));
                  console.log(
                    "Updated product data with new title:",
                    titleToTry
                  );
                }
              }

              // Update browser history state with product ID
              updateLocationState(response.data.id);
              if (showSuccessToast) {
                toast.success("Product draft saved successfully!");
              }
              break; // Success, exit the loop
            } catch (createError) {
              // Check if it's a title duplicate error
              const errorMessages = createError.response?.data?.errors;
              const isDuplicateTitle =
                errorMessages &&
                ((Array.isArray(errorMessages) &&
                  errorMessages.some((msg) =>
                    msg.includes("already been taken")
                  )) ||
                  (typeof errorMessages === "object" &&
                    Object.values(errorMessages).some(
                      (msg) =>
                        typeof msg === "string" &&
                        msg.includes("already been taken")
                    )));

              if (isDuplicateTitle && attempts < 2) {
                attempts++;
                continue; // Try again with modified title
              } else {
                throw createError; // Re-throw if not a duplicate or we've exhausted attempts
              }
            }
          }
        } else {
          // No title, just try to create
          response = await productSaveAsDraft(productPayload);
          if (response.data && response.data.id) {
            setSavedProductId(response.data.id);
            console.log("New product created with ID:", response.data.id);

            // Update the product data state if we have an override title
            if (overrideTitle) {
              setProductData((prev) => ({
                ...prev,
                title: overrideTitle,
                id: response.data.id,
              }));
              console.log(
                "Updated product data with new title:",
                overrideTitle
              );
            }
          }

          // Update browser history state with product ID
          updateLocationState(response.data.id);
          if (showSuccessToast) {
            toast.success("Product draft saved successfully!");
          }
        }
      }
      setError(null);
      
      // After successful save, refresh product data to get the latest saved data including collections and variants
      if (response && response.data && response.data.id) {
        try {
          const savedProductId = response.data.id;
          const refreshResponse = await fetchProductById(savedProductId);
          
          const refreshedProduct = refreshResponse?.data || refreshResponse;
          
          if (refreshedProduct) {
            
            // Parse foray_response if it exists
            if (refreshedProduct.foray_response) {
              try {
                const parsedForay = typeof refreshedProduct.foray_response === 'string' 
                  ? JSON.parse(refreshedProduct.foray_response)
                  : refreshedProduct.foray_response;
                
                // Update productData with refreshed data including collections and variants
                setProductData({
                  ...refreshedProduct,
                  ...parsedForay,
                  metafields: parsedForay.metafields || refreshedProduct.metafields || [],
                  collections: parsedForay.collections || refreshedProduct.collections || [],
                  variants: parsedForay.variants || refreshedProduct.variants || [],
                  foray_response: parsedForay,
                });
              } catch (parseError) {
                console.error("Failed to parse refreshed foray_response:", parseError);
                setProductData(refreshedProduct);
              }
            } else {
              setProductData(refreshedProduct);
            }
          }
        } catch (refreshError) {
          console.error("Failed to refresh product data after save:", refreshError);
          // Don't throw - the save was successful, just the refresh failed
        }
      }
    } catch (error) {
      const errorMessages = error.response?.data?.errors;
      let displayError = "Failed to save product as draft";
      if (errorMessages && Array.isArray(errorMessages)) {
        displayError = errorMessages.join("; ");
      } else if (typeof errorMessages === "object" && errorMessages !== null) {
        displayError = Object.entries(errorMessages)
          .map(([key, value]) => key + ": " + value)
          .join("; ");
      } else if (error.message) {
        displayError = error.message;
      }

      setError(displayError);
      toast.error(displayError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductRegenerate = async (
    section,
    furtherDetails,
    previousResponse,
    customPrompt = null
  ) => {
    setRegeneratingSection((prev) => [...prev, section]);
    console.log(
      "API CALL: handleProductRegenerate in EditProductDescription/index.jsx"
    );
  
    // Clear the previous data for the section being regenerated
    setProductData((prev) => {
      const newData = { ...prev };
      delete newData[section];
      return newData;
    });
    setAccumulatedStreamData((prev) => {
      const newData = { ...prev };
      delete newData[section];
      return newData;
    });
  
    try {
      setIsStreaming(true);
      setIsLoading(false);
      setStreamingLog([]);
      const languageValue = language?.label || language || "";
      const locationValue = locationFocus?.label || locationFocus || "";

      // Create a local variable to accumulate the regenerated section data
      let regeneratedSectionData = {};

      await generateProductDescriptionStream(
        initialSku,
        keywords,
        section,
        furtherDetails,
        activeAgentId,
        activeSessionId,
        (chunk) => {
          setStreamingLog((prev) => [...prev, chunk]);
          if (chunk.component_name && chunk.value !== undefined) {
            if (chunk.component_name === "ingredients") {
              if (typeof chunk.value === "string") {
                setIngredients(chunk.value);
              } else if (typeof chunk.value === "object") {
                const paragraph = Object.entries(chunk.value)
                  .map(([key, value]) => `${key}: ${value}\n`)
                  .join(" ");
                setIngredients(paragraph);
              }
              regeneratedSectionData["ingredients"] = chunk.value;
            } else if (chunk.component_name === "how_to_use") {
              if (typeof chunk.value === "string") {
                setHowToUse(chunk.value);
              } else if (typeof chunk.value === "object") {
                const paragraph = Object.entries(chunk.value)
                  .map(([key, value]) => `${key}: ${value}\n`)
                  .join(" ");
                setHowToUse(paragraph);
              }
              regeneratedSectionData["how_to_use"] = chunk.value;
            } else if (chunk.component_name === "dosage") {
              if (typeof chunk.value === "string") {
                setDosage(chunk.value);
              } else if (typeof chunk.value === "object") {
                const paragraph = Object.entries(chunk.value)
                  .map(([key, value]) => `${key}: ${value}\n`)
                  .join(" ");
                setDosage(paragraph);
              }
              regeneratedSectionData["dosage"] = chunk.value;
            } else {
              regeneratedSectionData[chunk.component_name] = chunk.value;
            }
            setProductData((prev) => ({
              ...prev,
              [chunk.component_name]: chunk.value,
            }));
          }
        },
        () => {
          setIsLoading(false);
          setIsStreaming(false);
          setRegeneratingSection((prev) => prev.filter((s) => s !== section));

          // Update the accumulatedStreamData with the regenerated section data
          setAccumulatedStreamData((prev) => {
            const updated = {
              ...prev,
              ...regeneratedSectionData,
            };
            return updated;
          });
        },
        (err) => {
          setError(err.message || "Streaming error");
          setIsLoading(false);
          setIsStreaming(false);
          setRegeneratingSection((prev) => prev.filter((s) => s !== section));
        },
        previousResponse,
        languageValue,
        locationValue,
        customPrompt
      );
    } catch (error) {
      setError(error.message || "Streaming error");
      setIsLoading(false);
      setIsStreaming(false);
      setRegeneratingSection((prev) => prev.filter((s) => s !== section));
    }
  };

  const handleEditManually = () => {
    setShowStoryModal(false);
    setShowEditForm(true);
  };

  const cleanText = (text) => {
    if (text) {
      return text
        .replace(/<[^>]+>/g, "")
        .replace(/\[\/?\w+\]/g, "")
        .trim();
    }
  };

  const cleanImageUrl = (url) => {
    if (typeof url === 'string') {
      return url.replace(/`/g, '').trim();
    }
    return url;
  };

  const cleanImageArray = (images) => {
    if (Array.isArray(images)) {
      return images.map(cleanImageUrl);
    }
    return images;
  };

  // Handler to update accumulatedStreamData when a new section is generated in ProductDetailsContainer
  const handleSectionUpdate = (action, { type, generatedContent, sectionOrder }) => {
    if (action === "add" && type && generatedContent !== undefined) {
      setAccumulatedStreamData((prev) => {
        const updated = {
          ...prev,
          [type]: generatedContent,
        };
        return updated;
      });

      if (
        type === "benefit" ||
        type === "benefits03" ||
        type === "benefits04" ||
        type.startsWith("benefit")
      ) {
        setProductData((prev) => ({
          ...prev,
          [type]: generatedContent,
        }));
      }
    } else if (action === "remove" && type) {
      // Remove section from accumulatedStreamData
      setAccumulatedStreamData((prev) => {
        const updated = { ...prev };
        delete updated[type];
        return updated;
      });

      // Also remove from productData if it's a benefit type
      if (
        type === "benefit" ||
        type === "benefits03" ||
        type === "benefits04" ||
        type.startsWith("benefit")
      ) {
        setProductData((prev) => {
          const updated = { ...prev };
          delete updated[type];
          return updated;
        });
      }
    } else if (action === "order" && sectionOrder) {
      // Update section order in accumulatedStreamData
      setAccumulatedStreamData((prev) => {
        const updated = {
          ...prev,
          sectionOrder: sectionOrder,
        };
        return updated;
      });
    }
  };

  // Handler to apply a sequence template
  const handleApplySequenceTemplate = (templateSectionOrder) => {
    if (!templateSectionOrder || !Array.isArray(templateSectionOrder)) {
      toast.error('Invalid template section order');
      return;
    }

    // Update the section order in accumulatedStreamData
    setAccumulatedStreamData((prev) => {
      const updated = {
        ...prev,
        sectionOrder: templateSectionOrder,
      };
      return updated;
    });

    // Trigger section update to propagate the order change
    handleSectionUpdate("order", { sectionOrder: templateSectionOrder });
  };

  // Helper function to analyze benefits structure and suggest cleanup
  const analyzeBenefitsStructure = (data) => {
    const analysis = {
      sectionBenefits: {},
      layoutSections: {},
      customSections: {},
      totalBenefits: 0,
      totalLayoutSections: 0,
      totalCustomSections: 0,
      suggestions: [],
    };

    // Find all benefits-related sections
    Object.keys(data).forEach((key) => {
      if (key.startsWith("benefits")) {
        analysis.sectionBenefits[key] = data[key];
        if (
          data[key] &&
          data[key].benefits &&
          Array.isArray(data[key].benefits)
        ) {
          analysis.totalBenefits += data[key].benefits.length;
        }
      }
      // Find layout-based sections
      else if (key.startsWith("layout_")) {
        analysis.layoutSections[key] = data[key];
        analysis.totalLayoutSections++;
      }
      // Find custom sections
      else if (key === "custom_section") {
        analysis.customSections[key] = data[key];
        analysis.totalCustomSections++;
      }
    });

    // Generate suggestions based on the structure
    if (Object.keys(analysis.sectionBenefits).length > 0) {
      analysis.suggestions.push(
        "Clean section-based benefits structure - matches UI layout"
      );
    }

    if (analysis.totalLayoutSections > 0) {
      analysis.suggestions.push(
        `${analysis.totalLayoutSections} layout-based sections included`
      );
    }

    if (analysis.totalCustomSections > 0) {
      analysis.suggestions.push(
        `${analysis.totalCustomSections} custom sections included`
      );
    }

    if (analysis.suggestions.length === 0) {
      analysis.suggestions.push("No sections found");
    }
  };
  const handleSaveSection = (section, data) => {
    const keyMap = {
      ingredients: "ingredients",
      howToUse: "how_to_use",
      dosage: "dosage",
    };
    const key = keyMap[section];
    if (key) {
      const setterMap = {
        ingredients: setIngredients,
        howToUse: setHowToUse,
        dosage: setDosage,
      };
      const setter = setterMap[section];
      if (setter) {
        setter(data);
      }

      setProductData((prevData) => ({
        ...prevData,
        [key]: data,
      }));
    }
  };

  // Helper function to update the URL with product ID parameter
  const updateLocationState = (productId) => {
    if (productId) {
      const newState = {
        ...location.state,
        productId: productId,
        productData: productData, // Include updated product data
      };

      // Navigate to URL with product ID parameter if not already there
      if (!urlProductId || urlProductId !== productId.toString()) {
        navigate(`/edit-product-description/${productId}`, {
          state: newState,
          replace: true // Replace current history entry to avoid creating new entry
        });
        console.log("Updated URL with productId:", productId);
      } else {
        // Just update the state if URL already has the correct product ID
        window.history.replaceState(
          newState,
          "",
          window.location.pathname + window.location.search
        );
        console.log("Updated browser history state with productId:", productId);
      }

      // Set flag to refresh product list when user navigates back
      localStorage.setItem("refreshProductList", "true");
    }
  };

  return (
    <div>
      {!previewMode && (
        <>
          <Breadcrumb />
          <div className="d-flex justify-content-between">
            <div className="title-label mx-3">Edit Product Description</div>
            <div className="btns d-flex gap-3">
              <div>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={() => setShowSequenceTemplateModal(true)}
                  title="Manage sequence templates"
                >
                  Sequence Templates
                </button>
              </div>
              <div>
                <button className="btn bgprimary" onClick={handleSaveAsDraft}>
                  Save as draft
                </button>
              </div>
              <div>
                <button className="btn btn-dark rounded">Push to shop</button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="container-fluid mt-3">
        <div className="row ">
          {!previewMode && (
            <>
              {" "}
              <div className="col-4" style={{ borderRight: "2px dashed #ccc" }}>
                <EditProductLeft
                  keywordsConfig={{
                    value: keywords,
                    onChange: handleKeywordsChange,
                    label: "What are the keywords you want to include ",
                    placeholder: "Enter custom keywords",
                    optimizeButtonText: "Optimize",
                    translateButtonText: "Translate",
                  }}
                  textareaConfig={{
                    value: details,
                    onChange: setDetails,
                    label: "Further details that you would like included here",
                    placeholder: "Enter custom details",
                    rows: 5,
                  }}
                  locationConfig={{
                    value: locationFocus,
                    onChange: setLocationFocus,
                    label: "Location Focus",
                    placeholder: "Type to search location...",
                  }}
                  languageConfig={{
                    value: language,
                    onChange: setLanguage,
                    label: "Language",
                    placeholder: "Type to search language...",
                  }}
                  onOptimize={handleOptimize}
                  onTranslate={onTranslate}
                  onAddDocuments={() => setShowModal(true)}
                  loading={isLoading}
                />
              </div>
            </>
          )}
          <div
            className={`${previewMode ? "col-12" : "col-8"} position-relative`}
            style={{ minHeight: "500px" }}
          >
            {isLoading && (
              <div
                className="position-absolute start-50 translate-middle d-flex justify-content-center align-items-center z-3"
                style={{
                  top: "10%",
                  width: "100%",
                  height: "200%",
                  background: "rgba(0, 0, 0, 0.47)",
                  backdropFilter: "blur(5px)",
                }}
              >
                <Loader />
              </div>
            )}

            <>
              {!previewMode ? (
                <div className="d-flex gap-4 align-items-center">
                  <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
                    Generated Product Details
                  </h5>
                  <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
                    <h6 className="mt-2 mx-1">Preview</h6>
                    <div className="d-flex justify-content-center align-items-center">
                      <button className="btn">
                        <BiLaptop
                          size={20}
                          onClick={() => handlePreviewToggle("desktop")}
                        />
                      </button>{" "}
                      |
                      <button className="btn">
                        <BiMobile
                          size={20}
                          onClick={() => handlePreviewToggle("mobile")}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="previewControl">
                  <button
                    className="back-btn"
                    onClick={() => handlePreviewToggle(null)}
                  >
                    &lt; Back to edit
                  </button>
                  <span className="preview-text">Preview</span>
                  <div className="view-toggle">
                    <button
                      className={`toggle-btn ${
                        previewMode === "desktop" ? "active" : ""
                      }`}
                      onClick={() => handlePreviewToggle("desktop")}
                    >
                      <BiLaptop size={20} />
                    </button>
                    <button
                      className={`toggle-btn ${
                        previewMode === "mobile" ? "active" : ""
                      }`}
                      onClick={() => handlePreviewToggle("mobile")}
                    >
                      <BiMobile size={20} />
                    </button>
                  </div>
                </div>
              )}
              {!previewMode && (
                <ProductImage
                  initialHeroImage={heroImage || initialProductData?.image}
                  initialProductImages={
                    productImages || initialProductData?.images
                  }
                  previewMode={previewMode}
                  onHeroImageChange={handleHeroImageChange}
                  onProductImagesChange={handleProductImagesChange}
                />
              )}

              {(productData || initialProductData) && (
                <ProductDetailsContainer
                  key={renderKey}
                  productabout={(productData || initialProductData)?.teaser}
                  productname={
                    // // Priority 1: Title from foray_response (parsed data)
                    // accumulatedStreamData?.title ||
                    // Priority 2: Title from productData/initialProductData
                    (productData || initialProductData)?.title
                  }
                  productdescription={cleanText(
                    (productData || initialProductData)?.description
                  )}
                  benifitss={(productData || initialProductData)?.highlights}
                  handleProductRegenerate={handleProductRegenerate}
                  ingredients={
                    (productData || initialProductData)?.ingredients ||
                    ingredients ||
                    ""
                  }
                  howToUse={
                    (productData || initialProductData)?.how_to_use ||
                    howToUse ||
                    ""
                  }
                  dosage={
                    (productData || initialProductData)?.dosage || dosage || ""
                  }
                  previewMode={previewMode} heroImage={(productData || initialProductData)?.image}
                  productImages={(productData || initialProductData)?.images || []}
                  language={language}
                  locationFocus={locationFocus}
                  initialSku={initialSku}
                  keywords={keywords}
                  furtherDetails={details}
                  agentId={activeAgentId}
                  sessionId={activeSessionId}
                  isStreaming={isStreaming}
                  onSectionUpdate={handleSectionUpdate}
                  accumulatedStreamData={accumulatedStreamData}
                  regeneratingSection={regeneratingSection}
                  onSave={handleSaveSection}
                  handleManualEdit={(componentName, value) =>
                    setProductData((prev) => ({
                      ...prev,
                      [componentName]: value,
                    }))
                  }
                  productId={initialProductId}
                  productData={productData || initialProductData}
                  onGetCurrentSectionOrder={(sectionOrder) => {
                    setCurrentSectionOrder(sectionOrder);
                  }}
                  metafields={(() => {
                    const currentData = productData || initialProductData;
                    const metafields = currentData?.metafields;
                    return metafields;
                  })()}
                  collections={(() => {
                    const currentData = productData || initialProductData;
                    return currentData?.collections || [];
                  })()}
                 // variants={(() => {
                   // const currentData = productData || initialProductData;
                //    return currentData?.variants || [];
                 // })()}
                />
              )}
              {/* Loader at the bottom for streaming */}
              {/* {isStreaming && (
                            <div className="d-flex flex-column align-items-center w-100 mt-3 mb-2">
                                <div className="d-flex align-items-center">
                                    <Loader />
                                    <span className="ms-2">Loading product details...</span>
                                </div>
                              
                            </div>
                        )} */}
            </>
          </div>
        </div>
        {!previewMode && (
          <div className="btns d-flex justify-content-end gap-3 mt-5">
            <div className="align-content-center">
              {responseId && showFeedback && (
                <div className="d-flex gap-3 align-items-center justify-content-center">
                  <span>Did you find this result helpful?</span>
                  <FaRegThumbsUp
                    size={20}
                    style={{ cursor: "pointer", color: "#f87171" }}
                    onClick={() => setShowFeedback(false)}
                  />
                  <FaRegThumbsDown
                    size={20}
                    style={{ cursor: "pointer", color: "#f87171" }}
                    onClick={() => setShowFeedback(false)}
                  />
                </div>
              )}
            </div>
            {/* <div>
                        <button className="btn bgprimary rounded">Save as draft</button>
                    </div> */}
            {/* <div>
                        <button className="btn btn-dark rounded">Push to shop</button>
                    </div> */}
          </div>
        )}
      </div>
      <UnifiedDocumentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        sku={initialSku}
      />

      <SequenceTemplateModal
        show={showSequenceTemplateModal}
        onHide={() => setShowSequenceTemplateModal(false)}
        currentSectionOrder={currentSectionOrder.length > 0 ? currentSectionOrder : (accumulatedStreamData?.sectionOrder || [])}
        productId={savedProductId || urlProductId || initialProductId}
        onApplyTemplate={handleApplySequenceTemplate}
      />

      <NameChangeModal
        show={showNameChangeModal}
        onHide={() => setShowNameChangeModal(false)}
        onConfirm={handleNameChangeConfirm}
        currentName={productData?.title || initialProductData?.title || ""}
      />

      <LocalDraftModal
        show={showLocalDraftModal}
        onHide={() => setShowLocalDraftModal(false)}
        onConfirm={handleLocalDraftConfirm}
        isChecking={isLoading}
      />
    </div>
  );
};

export default EditProductDescription;